import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getPlans, notifyPurchaseInterest } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Check, ArrowRight, Loader2, X, Mail, ShoppingCart, Clock3, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const SALES_EMAIL = 'info@tecnologieverona.it';

export default function AccountSubscription() {
  const { user, brand, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState(null);
  const [purchaseModal, setPurchaseModal] = useState(null); // {planId, planInfo}
  const [notifying, setNotifying] = useState(false);
  const [notifySent, setNotifySent] = useState(false);
  const [notifyError, setNotifyError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/login'); return; }
    getPlans().then(setPlans);
  }, [user, authLoading, nav]);

  const openPurchaseModal = (planId, planInfo) => {
    setPurchaseModal({ planId, planInfo });
    setNotifySent(false);
    setNotifyError(null);
  };

  const closePurchaseModal = () => {
    if (notifying) return;
    setPurchaseModal(null);
    setNotifySent(false);
    setNotifyError(null);
  };

  const sendPurchaseInterest = async () => {
    if (!purchaseModal) return;
    setNotifying(true);
    setNotifyError(null);
    try {
      const res = await notifyPurchaseInterest(purchaseModal.planId);
      if (res.ok) {
        setNotifySent(true);
        toast.success('Richiesta inviata', { description: `Ti contatteremo a breve all'indirizzo ${user?.email || ''}.` });
      } else {
        setNotifyError(res.error || 'Impossibile inviare la notifica. Riprova o scrivi a info@tecnologieverona.it');
      }
    } catch (e) {
      setNotifyError(e?.response?.data?.detail || 'Errore di rete. Scrivi direttamente a info@tecnologieverona.it');
    } finally {
      setNotifying(false);
    }
  };

  if (!plans || !brand) return (
    <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" /></div>
  );

  const current = brand.plan;

  return (
    <div className="py-10">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0d63b0]">Abbonamento</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900">Il tuo piano</h1>
          <p className="mt-1 text-slate-600 text-sm">Attualmente sei sul piano <span className="font-bold capitalize text-slate-900">{current}</span>. Cambia piano quando vuoi.</p>
        </div>

        <Card data-testid="dev-payment-banner" className="border border-amber-200 bg-amber-50/70 p-4 mb-6 flex items-start gap-3">
          <Clock3 className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Acquisto online in fase di sviluppo.</strong> Il checkout automatico non è ancora attivo. Per attivare un piano a pagamento clicca <em>“Richiedi attivazione”</em> qui sotto: riceveremo la tua richiesta via email e ti contatteremo per finalizzare. In alternativa scrivi a <a href={`mailto:${SALES_EMAIL}`} className="underline font-semibold">{SALES_EMAIL}</a>.
          </div>
        </Card>

        <div className="grid md:grid-cols-3 gap-5">
          {Object.entries(plans).map(([id, p]) => {
            const active = id === current;
            const isPaid = id !== 'free';
            return (
              <Card data-testid={`plan-card-${id}`} key={id} className={`p-6 border ${active ? 'border-[#0d63b0] ring-2 ring-[#0d63b0]/15' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-slate-900 text-lg">{p.name}</div>
                  {active && <span className="text-[11px] bg-[#0d63b0] text-white rounded-full px-2 py-0.5 font-semibold">ATTIVO</span>}
                </div>
                <div className="mt-3 text-3xl font-extrabold text-slate-900">€{p.price_eur}<span className="ml-1 text-sm text-slate-500 font-normal">/mese</span></div>
                <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                  <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" /> File fino a {p.max_file_mb}MB</li>
                  <li className="flex items-center gap-1.5">{p.custom_branding ? <Check className="h-4 w-4 text-emerald-600" /> : <span className="h-4 w-4 text-center text-slate-300">–</span>} Branding personalizzato</li>
                  <li className="flex items-center gap-1.5">{p.custom_domain ? <Check className="h-4 w-4 text-emerald-600" /> : <span className="h-4 w-4 text-center text-slate-300">–</span>} Dominio custom + white-label</li>
                </ul>
                {active ? (
                  <Button disabled className="mt-5 w-full bg-slate-200 text-slate-700 hover:bg-slate-200">Piano attuale</Button>
                ) : (
                  <div className="mt-5">
                    {isPaid ? (
                      <Button
                        data-testid={`plan-purchase-btn-${id}`}
                        onClick={() => openPurchaseModal(id, p)}
                        className="w-full bg-[#0d63b0] hover:bg-[#0a4f8f] text-white"
                      >
                        <ShoppingCart className="h-4 w-4 mr-1.5" />
                        Richiedi attivazione · €{p.price_eur}/mese
                      </Button>
                    ) : (
                      <Button disabled variant="outline" className="w-full opacity-60 cursor-not-allowed">
                        Piano gratuito
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Purchase interest modal */}
        {purchaseModal && (
          <div
            data-testid="purchase-interest-modal"
            className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
            onClick={closePurchaseModal}
          >
            <Card className="max-w-md w-full p-6 border border-slate-200 bg-white relative animate-fade-up" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid="purchase-modal-close"
                onClick={closePurchaseModal}
                disabled={notifying}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 disabled:opacity-40"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>

              {!notifySent ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                      <Clock3 className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-900">Acquisto in fase di sviluppo</div>
                      <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">Attivazione manuale</div>
                    </div>
                  </div>

                  <p className="mt-5 text-sm text-slate-700 leading-relaxed">
                    L'acquisto online è in fase di sviluppo. Se sei interessato al piano <strong className="text-slate-900">{purchaseModal.planInfo.name}</strong> (€{purchaseModal.planInfo.price_eur}/mese), invieremo automaticamente una notifica al team commerciale che ti contatterà per finalizzare.
                  </p>

                  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                    <div className="flex justify-between"><span>Piano</span><span className="font-semibold text-slate-900">{purchaseModal.planInfo.name}</span></div>
                    <div className="flex justify-between"><span>Prezzo</span><span className="font-mono font-semibold text-slate-900">€{purchaseModal.planInfo.price_eur.toFixed(2)}/mese</span></div>
                    <div className="flex justify-between"><span>Ti contatteremo a</span><span className="font-mono text-slate-800 truncate max-w-[180px]">{user?.email || '—'}</span></div>
                  </div>

                  <p className="mt-4 text-xs text-slate-500 flex items-start gap-1.5">
                    <Mail className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    In alternativa puoi scrivere direttamente a <a href={`mailto:${SALES_EMAIL}`} className="text-[#0d63b0] font-semibold underline">{SALES_EMAIL}</a>
                  </p>

                  {notifyError && (
                    <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      {notifyError}
                    </div>
                  )}

                  <div className="mt-5 flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={closePurchaseModal} disabled={notifying} data-testid="purchase-modal-cancel">
                      Annulla
                    </Button>
                    <Button
                      data-testid="purchase-modal-confirm"
                      onClick={sendPurchaseInterest}
                      disabled={notifying}
                      className="flex-1 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white"
                    >
                      {notifying ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Mail className="h-4 w-4 mr-1.5" /> Invia richiesta</>)}
                    </Button>
                  </div>
                </>
              ) : (
                <div data-testid="purchase-modal-success" className="text-center py-2">
                  <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-900">Richiesta inviata</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Abbiamo ricevuto la tua richiesta per il piano <strong>{purchaseModal.planInfo.name}</strong>. Il team commerciale ti contatterà a breve all'indirizzo <span className="font-mono text-slate-900">{user?.email}</span>.
                  </p>
                  <Button data-testid="purchase-modal-close-success" onClick={closePurchaseModal} className="mt-5 w-full bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                    Ho capito
                  </Button>
                </div>
              )}
            </Card>
          </div>
        )}

        <div className="mt-8 text-sm text-slate-600 flex items-center gap-2">
          Torna a <Link to="/account/brand" className="text-[#0d63b0] font-semibold hover:underline inline-flex items-center gap-1">personalizzare il brand <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
      </div>
    </div>
  );
}
